import { useMemo } from 'react';

import { IconAppConnect } from '@components/icons/app-connect';
import { IconStatisticsBar } from '@components/icons/icon-statistics-bar';
import { IconInfo } from '@components/icons/info';
import { IconJobboard } from '@components/icons/jobboard';
import { IconList } from '@components/icons/list';
import { IconStages } from '@components/icons/stages';
import { IconTeam } from '@components/icons/team';
import { useIsMobile } from '@hooks/use-is-mobile';

import { useFeature } from 'features/authentication/authentication-queries';
import { usePermissions } from 'features/permissions';
import { useIsJourneysEnabled } from 'integrations/journeys/hooks';
import { useJobTriggerCount } from 'integrations/journeys/job-triggers/hooks';
import { useStore } from 'models/Provider';

export const useJobTabs = (job) => {
  const { t } = useStore();
  const isMobile = useIsMobile();
  const { hasRolePermission } = usePermissions();
  const hasJourneysIntegrationFlag = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  const isJourneysEnabled = useIsJourneysEnabled();
  // Flag AND role AND connected — the tab and its badge query must agree. The
  // role differs from useHasJourneysAutomation on purpose: viewing this tab is
  // job:journeys, managing triggers is application:add-journeys.
  const hasJourneysAutomation = Boolean(
    hasJourneysIntegrationFlag && hasRolePermission('job:journeys') && isJourneysEnabled,
  );

  const triggerCountQuery = useJobTriggerCount(job.id, { enabled: hasJourneysAutomation });
  const triggerCount = hasJourneysAutomation ? (triggerCountQuery.data?.count ?? 0) : 0;
  // Show the counter even at 0, per the acceptance criterion, but only once the
  // count has actually loaded — otherwise the tab paints a 0 badge and then
  // jumps to the real number on first render.
  const isTriggerCountKnown = hasJourneysAutomation && triggerCountQuery.data !== undefined;

  return useMemo(() => {
    if (isMobile) {
      return [
        {
          display: t('job.tabs.pipeline'),
          value: 'pipeline',
          link: job.pipelineUrl,
          icon: <IconStages />,
        },
        {
          display: t('job.tabs.applicants'),
          value: 'list',
          link: job.applicantsUrl,
          icon: <IconList />,
        },
        hasRolePermission('job:statistics') && {
          display: t('job.tabs.statistics'),
          value: 'statistics',
          link: job.statisticsUrl,
          icon: <IconStatisticsBar />,
        },
      ].filter(Boolean);
    }
    return [
      {
        display: t('job.tabs.pipeline'),
        value: 'pipeline',
        link: job.pipelineUrl,
        icon: <IconStages />,
      },
      {
        display: t('job.tabs.applicants'),
        value: 'list',
        link: job.applicantsUrl,
        icon: <IconList />,
      },
      { display: t('job.tabs.info'), value: 'info', link: job.jobInfoUrl, icon: <IconInfo /> },
      hasRolePermission('job:access') && {
        display: t('job.tabs.access'),
        value: 'access',
        link: job.accessUrl,
        icon: <IconTeam />,
      },
      hasRolePermission('job:statistics') && {
        display: t('job.tabs.statistics'),
        value: 'statistics',
        link: job.statisticsUrl,
        icon: <IconStatisticsBar />,
      },
      hasRolePermission('job:apps') && {
        display: t('job.tabs.integrations'),
        value: 'integrations',
        link: job.integrationsUrl,
        icon: <IconAppConnect />,
      },
      hasRolePermission('job:promote') && {
        display: t('job.tabs.promote'),
        value: 'promote',
        link: job.promoteUrl,
        icon: <IconJobboard />,
      },
      hasJourneysAutomation && {
        display: t('job.tabs.journeys'),
        value: 'journeys',
        link: job.journeysUrl,
        badge: isTriggerCountKnown ? triggerCount : null,
      },
    ].filter(Boolean);
  }, [
    t,
    job,
    isMobile,
    hasRolePermission,
    hasJourneysAutomation,
    triggerCount,
    isTriggerCountKnown,
  ]);
};
